// src/components/ObjectiveBoard/ObjectiveCard.js
// --- MODIFIED FILE ---
import React, { useState, useRef, useEffect } from "react";
import { Trash2, Wand2, Loader2, Plus, CornerDownLeft, Calendar, Bell, BellOff, Flag } from 'lucide-react';
import { apiService } from "../../services/api";

const priorityMap = {
    urgent: { label: 'Urgente', color: 'bg-red-500' },
    high: { label: 'Haute', color: 'bg-orange-400' },
    medium: { label: 'Moyenne', color: 'bg-yellow-400' },
    low: { label: 'Basse', color: 'bg-blue-400' },
};

const ObjectiveCard = ({
  objective,
  editing,
  handleEditingChange,
  handleEditingBlur,
  handleSelectChange,
  enableEditing,
  getOwnerInitials,
  viewMode,
  getProjectName,
  getProjectPriorityInfo,
  navigate,
  getSprintName,
  sprintOptions,
  employees,
  getOwnerName,
  handleDeleteObjective,
  handleDragStart,
  onRequestSuggestion,
  onUpdateObjective,
  onToggleSubscription,
  currentUserId
}) => {
  const [isRequestingSuggestion, setIsRequestingSuggestion] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState("");
  const [editingTask, setEditingTask] = useState({ taskId: null, field: null, value: '' });
  
  const subscriberIds = Array.isArray(objective.subscriber_ids) ? objective.subscriber_ids : [];
  const currentUserIdRef = currentUserId != null ? String(currentUserId) : null;
  const isSubscribed = currentUserIdRef ? subscriberIds.some(id => String(id) === currentUserIdRef) : false;
  const subscriptionCount = subscriberIds.length;

  const cardRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const expandTimeoutRef = useRef(null);

  const handleSubscriptionToggle = (event) => {
    event.stopPropagation();
    if (onToggleSubscription) {
      onToggleSubscription(objective);
    }
  };
  
  const handleMouseEnter = () => {
    clearTimeout(hideTimeoutRef.current);
    if (!isExpanded) {
      expandTimeoutRef.current = setTimeout(() => setIsExpanded(true), 350);
    }
  };

  const handleMouseLeave = () => {
    clearTimeout(expandTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setIsExpanded(false);
    }, 500);
  };

  useEffect(() => {
    return () => {
      clearTimeout(hideTimeoutRef.current);
      clearTimeout(expandTimeoutRef.current);
    };
  }, []);

  const saveTaskField = async (taskId, field, value) => {
    if (taskId === null || field === null) return;

    const originalTask = objective.tasks.find(t => t.id === taskId);
    if (!originalTask) return;

    let newValue = value === '' ? null : value;
    if (field === 'owner_id' && newValue !== null) {
      newValue = Number(newValue);
    }
    
    // Si la valeur a réellement changé, faire l'appel API
    if (String(originalTask[field] || (field === 'owner_id' || field === 'due_date' ? null : '')) !== String(newValue)) {
      try {
        const payload = { [field]: newValue };
        const updatedTask = await apiService.updateTask(taskId, payload);
        const updatedTasks = objective.tasks.map(t => (t.id === updatedTask.id ? updatedTask : t));
        onUpdateObjective({ ...objective, tasks: updatedTasks });
      } catch (error) {
        console.error(`Erreur mise à jour du champ ${field} de la tâche:`, error);
      }
    }

    // Quitter le mode édition
    setEditingTask({ taskId: null, field: null, value: '' });
  };

  const handleSaveTaskFieldOnChange = (e) => {
    const { taskId, field } = editingTask;
    const value = e.target.value;
    saveTaskField(taskId, field, value);
  };

  const handleSaveTaskFieldOnBlur = () => {
    const { taskId, field, value } = editingTask;
    saveTaskField(taskId, field, value);
  };

  const handleWandClick = async (e) => {
    e.stopPropagation();
    if (isRequestingSuggestion || !onRequestSuggestion) return;
    setIsRequestingSuggestion(true);
    try {
      await onRequestSuggestion(objective.id, objective.name);
    } catch (error) {
      console.error("Erreur de suggestion:", error);
    } finally {
      setIsRequestingSuggestion(false);
    }
  };

  const handleToggleTask = async (task) => {
    try {
      const updatedTask = await apiService.updateTask(task.id, { is_completed: !task.is_completed });
      const updatedTasks = objective.tasks.map(t => t.id === updatedTask.id ? updatedTask : t);
      onUpdateObjective({ ...objective, tasks: updatedTasks });
    } catch (error) {
      console.error("Erreur mise à jour tâche:", error);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskName.trim()) {
      setIsAddingTask(false);
      setNewTaskName("");
      return;
    }
    try {
      const createdTask = await apiService.createTaskForObjective(objective.id, { name: newTaskName.trim() });
      onUpdateObjective({ ...objective, tasks: [...(objective.tasks || []), createdTask] });
      setNewTaskName("");
      setIsAddingTask(false);
    } catch (error) {
      console.error("Erreur création tâche:", error);
    }
  };

  const handleEditTaskField = (task, field) => {
    let initialValue = task[field] || '';
    if (field === 'due_date' && initialValue) {
      initialValue = new Date(initialValue).toISOString().split('T')[0];
    }
    setEditingTask({ taskId: task.id, field, value: initialValue });
  };

  const projectName = getProjectName(objective.project_id) || "Projet";
  const projectPriorityInfo = getProjectPriorityInfo ? getProjectPriorityInfo(objective.project_id) : null;
  const sprintLabel = objective.sprint_id ? getSprintName(objective.sprint_id) : "Backlog";
  const tasks = Array.isArray(objective.tasks) ? objective.tasks : [];
  const completedTasks = tasks.filter(task => task.is_completed).length;
  const tasksProgress = tasks.length ? Math.round((completedTasks / tasks.length) * 100) : 0;
  const tasksProgressLabel = tasks.length
    ? `${completedTasks}/${tasks.length} tâches`
    : "0 tâche";
  const statusStyles = {
    not_started: { label: "À lancer", badge: "bg-slate-100 text-slate-700" },
    in_progress: { label: "En cours", badge: "bg-indigo-100 text-indigo-700" },
    blocked: { label: "Bloqué", badge: "bg-rose-100 text-rose-700" },
    done: { label: "Livré", badge: "bg-emerald-100 text-emerald-700" }
  };
  const statusToken = statusStyles[objective.status] || statusStyles.not_started;
  const isMilestone = Boolean(objective.is_milestone && objective.milestone_date);
  const milestoneDateLabel = isMilestone
    ? new Date(`${String(objective.milestone_date).split("T")[0]}T00:00:00`).toLocaleDateString(
        "fr-FR",
        { day: "2-digit", month: "short", year: "numeric" },
      )
    : null;

  return (
    <div
      ref={cardRef}
      key={objective.id}
      className={`group/objectiveitem relative mb-3 flex w-full cursor-move flex-col rounded-3xl border border-slate-100 bg-white/95 px-3 pt-3 pb-2 shadow-[0_10px_35px_rgba(15,23,42,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(15,23,42,0.12)] ${
        isExpanded ? "ring-1 ring-indigo-100" : ""
      }`}
      draggable
      onDragStart={(e) => handleDragStart(e, objective)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="absolute inset-x-6 top-3 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent opacity-70" />
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2 text-[11px] font-semibold text-slate-500">
          <span
            className="cursor-pointer rounded-full bg-slate-100 px-3 py-0.5 text-slate-600 transition hover:bg-slate-200"
            onClick={() =>
              navigate && typeof navigate === "function" ? navigate(`/project/${objective.project_id}`) : null
            }
          >
            {projectName}
          </span>
          {projectPriorityInfo && (
            <span
              className={`rounded-full border px-2.5 py-0.5 ${projectPriorityInfo.className}`}
              title={`Priorite projet : P${projectPriorityInfo.value} - ${projectPriorityInfo.label}`}
            >
              P{projectPriorityInfo.value} {projectPriorityInfo.label}
            </span>
          )}
          <div className="flex items-center gap-2">
            {viewMode !== "myObjectives" && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  enableEditing(objective, "owner_id");
                }}
                className={`rounded-full bg-slate-100 px-3 py-0.5 transition hover:bg-slate-200 ${
                  objective.owner_id ? "text-slate-700" : "text-slate-400"
                }`}
                title="Modifier l'owner"
              >
                {editing.objectiveId === objective.id && editing.field === "owner_id" ? (
                  <select
                    value={editing.value}
                    onChange={(e) => handleSelectChange(objective.id, "owner_id", e.target.value)}
                    className="rounded border border-slate-200 text-xs"
                    autoFocus
                  >
                    <option value="">--</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name.split(" ")[0]}
                      </option>
                    ))}
                  </select>
                ) : (
                  objective.owner_id ? getOwnerInitials(objective.owner_id) : "Assigner"
                )}
              </button>
            )}
            {!isMilestone && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  enableEditing(objective, "effort");
                }}
                className="rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 px-2 py-1 text-xs font-semibold text-white shadow-inner"
              >
                {editing.objectiveId === objective.id && editing.field === "effort" ? (
                  <input
                    type="number"
                    value={editing.value}
                    onChange={(e) => {
                      handleEditingChange(e);
                      handleSelectChange(objective.id, "effort", e.target.value);
                    }}
                    onBlur={handleEditingBlur}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.target.blur();
                    }}
                    className="w-12 rounded border border-slate-200 px-1 py-0.5 text-xs text-slate-900"
                    autoFocus
                  />
                ) : (
                  objective.effort || 0
                )}
              </button>
            )}
          </div>
        </div>

        <div className="min-w-0 space-y-1 pr-8">
          {editing.objectiveId === objective.id && editing.field === "name" ? (
            <input
              type="text"
              value={editing.value}
              onChange={handleEditingChange}
              onBlur={handleEditingBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.target.blur();
              }}
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm font-semibold leading-snug"
              autoFocus
            />
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                enableEditing(objective, "name");
              }}
              className="text-left text-[16px] font-semibold leading-snug text-slate-900 transition hover:text-indigo-600 break-words"
            >
              {objective.name}
            </button>
          )}
          {!isExpanded && objective.description && (
            <p
              className="text-[12px] text-slate-600 leading-snug break-words"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}
            >
              {objective.description}
            </p>
          )}
        </div>

        <div className="mt-1 flex flex-wrap gap-1 text-[11px] font-semibold text-slate-500">
          {isMilestone ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-0.5 text-rose-700 transition hover:bg-rose-100"
              onClick={(event) => {
                event.stopPropagation();
                enableEditing(objective, "milestone_date");
              }}
              title="Modifier la date du jalon"
            >
              {editing.objectiveId === objective.id && editing.field === "milestone_date" ? (
                <input
                  type="date"
                  value={editing.value}
                  onChange={handleEditingChange}
                  onBlur={handleEditingBlur}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.currentTarget.blur();
                  }}
                  className="rounded border border-rose-200 bg-white px-1 text-[11px] text-slate-700"
                  onClick={(event) => event.stopPropagation()}
                  autoFocus
                />
              ) : (
                <>
                  <Flag className="h-3 w-3" />
                  Jalon · {milestoneDateLabel}
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                enableEditing(objective, "sprint_id");
              }}
              className="rounded-full bg-white/80 px-2.5 py-0.5 text-slate-500 shadow-inner transition hover:bg-white"
            >
              {editing.objectiveId === objective.id && editing.field === "sprint_id" ? (
                <select
                  value={editing.value}
                  onChange={(e) => handleSelectChange(objective.id, "sprint_id", e.target.value)}
                  className="rounded border border-slate-200 p-1 text-[10px]"
                  autoFocus
                >
                  <option value="">Backlog</option>
                  {sprintOptions.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.description || `Sprint ${sp.id}`}
                    </option>
                  ))}
                </select>
              ) : (
                sprintLabel
              )}
            </button>
          )}
          <span className={`rounded-full px-2.5 py-0.5 font-semibold leading-tight ${statusToken.badge}`}>
            {statusToken.label}
          </span>
          <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-indigo-700">{tasksProgressLabel}</span>
        </div>
        <div className="pointer-events-none absolute bottom-1 right-2 flex gap-1 text-[11px]">
          <button
            onClick={handleWandClick}
            disabled={isRequestingSuggestion}
            className="pointer-events-auto rounded-full bg-white/80 p-1.5 text-indigo-500 opacity-0 transition group-hover/objectiveitem:opacity-100 hover:bg-indigo-50"
            title="Suggérer une reformulation"
          >
            {isRequestingSuggestion ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          </button>
          <button
            onClick={handleSubscriptionToggle}
            className={`pointer-events-auto rounded-full p-1.5 opacity-0 transition group-hover/objectiveitem:opacity-100 ${
              isSubscribed ? "text-blue-600 hover:bg-blue-50" : "text-slate-400 hover:bg-slate-100"
            }`}
            title={isSubscribed ? "Se désabonner de l'objectif" : "S'abonner à l'objectif"}
          >
            {isSubscribed ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
          </button>
          {subscriptionCount > 0 && (
            <span className="pointer-events-none text-[10px] font-semibold text-slate-400">{subscriptionCount}</span>
          )}
        </div>
      </div>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? "mt-4 max-h-[520px] border-t border-slate-100 pt-4" : "max-h-0"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div
            className="flex-1 cursor-pointer text-sm text-slate-600"
            onClick={(e) => {
              e.stopPropagation();
              enableEditing(objective, "description");
            }}
          >
            {editing.objectiveId === objective.id && editing.field === "description" ? (
              <textarea
                value={editing.value}
                onChange={handleEditingChange}
                onBlur={handleEditingBlur}
                className="w-full rounded-2xl border border-slate-200 p-2 text-sm"
                autoFocus
              />
            ) : (
              objective.description || <span className="text-xs italic text-slate-400">Pas de description</span>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteObjective(objective.id);
            }}
            className="rounded-full bg-rose-50 p-2 text-rose-500 opacity-0 transition group-hover/objectiveitem:opacity-100 hover:bg-rose-100"
            title="Supprimer l'objectif"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 rounded-2xl bg-slate-50/80 p-3">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
            <span>Tâches</span>
            {tasks.length > 0 && (
              <span className="text-[11px] text-slate-400">
                {completedTasks}/{tasks.length}
              </span>
            )}
          </div>
          <div className="mt-2 space-y-2">
            {tasks.map((task) => {
              const priorityInfo = task.priority ? priorityMap[task.priority] : null;
              const isDueDatePast = task.due_date && new Date(task.due_date) < new Date() && !task.is_completed;
              return (
                <div key={task.id} className="group/taskitem flex items-center justify-between rounded-2xl bg-white/90 px-2 py-1 text-sm shadow-sm">
                  <div className="flex min-w-0 flex-1 items-center">
                    <input
                      type="checkbox"
                      checked={task.is_completed}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleTask(task);
                      }}
                      className="h-4 w-4 flex-shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    {editingTask.taskId === task.id && editingTask.field === "name" ? (
                      <input
                        type="text"
                        value={editingTask.value}
                        onChange={(e) => setEditingTask((prev) => ({ ...prev, value: e.target.value }))}
                        onBlur={handleSaveTaskFieldOnBlur}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveTaskFieldOnBlur();
                          if (e.key === "Escape") setEditingTask({ taskId: null, field: null, value: "" });
                        }}
                        className="ml-2 w-full border-b border-indigo-200 bg-transparent text-sm focus:border-indigo-500 focus:outline-none"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTaskField(task, "name");
                        }}
                        className={`ml-2 truncate ${task.is_completed ? "text-slate-400 line-through" : "text-slate-800"}`}
                        title={task.name}
                      >
                        {task.name}
                      </span>
                    )}
                  </div>
                  <div className="ml-2 flex flex-shrink-0 items-center space-x-2">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditTaskField(task, "priority");
                      }}
                    >
                      {editingTask.taskId === task.id && editingTask.field === "priority" ? (
                        <select
                          value={editingTask.value || ""}
                          onChange={handleSaveTaskFieldOnChange}
                          onBlur={handleSaveTaskFieldOnBlur}
                          autoFocus
                          className="h-5 rounded border text-xs"
                        >
                          <option value="">--</option>
                          <option value="low">Basse</option>
                          <option value="medium">Moyenne</option>
                          <option value="high">Haute</option>
                          <option value="urgent">Urgente</option>
                        </select>
                      ) : priorityInfo ? (
                        <div title={`Priorité: ${priorityInfo.label}`} className={`h-4 w-4 cursor-pointer rounded ${priorityInfo.color}`} />
                      ) : (
                        <div title="Définir la priorité" className="h-4 w-4 cursor-pointer rounded border border-dashed border-slate-400" />
                      )}
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditTaskField(task, "due_date");
                      }}
                    >
                      {editingTask.taskId === task.id && editingTask.field === "due_date" ? (
                        <input
                          type="date"
                          value={editingTask.value}
                          onChange={(e) => setEditingTask((prev) => ({ ...prev, value: e.target.value }))}
                          onBlur={handleSaveTaskFieldOnBlur}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === "Escape") e.target.blur();
                          }}
                          autoFocus
                          className="h-5 rounded border text-xs"
                        />
                      ) : (
                        <div className={`flex items-center text-xs ${isDueDatePast ? "font-semibold text-rose-500" : "text-slate-500"}`}>
                          <Calendar size={12} className="mr-0.5" />
                          {task.due_date
                            ? new Date(task.due_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })
                            : <span className="text-slate-300">Date</span>}
                        </div>
                      )}
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditTaskField(task, "owner_id");
                      }}
                    >
                      {editingTask.taskId === task.id && editingTask.field === "owner_id" ? (
                        <select
                          value={editingTask.value || ""}
                          onChange={handleSaveTaskFieldOnChange}
                          onBlur={handleSaveTaskFieldOnBlur}
                          autoFocus
                          className="h-5 rounded border text-xs"
                        >
                          <option value="">--</option>
                          {employees.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.name.split(" ")[0]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div
                          className="flex h-5 w-5 items-center justify-center rounded-md bg-slate-200 text-[10px] text-slate-700"
                          title={task.owner_id ? getOwnerName(task.owner_id) : "Assigner un owner"}
                        >
                          {task.owner_id ? getOwnerInitials(task.owner_id) : "?"}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {isAddingTask ? (
              <form onSubmit={handleAddTask} className="mt-2 flex items-center rounded-2xl bg-white px-2 py-1 shadow-inner">
                <input
                  type="text"
                  value={newTaskName}
                  onChange={(e) => setNewTaskName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setIsAddingTask(false);
                  }}
                  placeholder="Nouvelle tâche..."
                  className="w-full border-b border-transparent text-sm focus:border-indigo-500 focus:outline-none"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
                <button type="submit" className="p-1 text-emerald-500 hover:text-emerald-600">
                  <CornerDownLeft size={16} />
                </button>
              </form>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAddingTask(true);
                }}
                className="mt-2 flex items-center text-xs font-semibold text-indigo-600 hover:text-indigo-800"
              >
                <Plus size={14} className="mr-1" /> Ajouter une tâche
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObjectiveCard;
